import { EntityList } from '@/common/entityFramework/entityList/EntityList';
import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { useCallback, useRef } from 'react';

export const ConfigList = ({ parentConfigId, parentOrgId, fullHistory = false, showTitle = true }) => {
  const rsrc = useRsrc();
  const module = useModuleFromRoute();
  const { navigateTo } = useNavState();
  const entityListRef = useRef();
  const hiddenFields = new Set(['checkboxSelection', 'code', 'recBy', 'id', 'sor', 'srcSys', 'ref']);

  const augFieldDefs = useCallback(
    (fieldToFieldDef) => {
      Object.values(fieldToFieldDef).forEach((fieldDef) => {
        if (hiddenFields.has(fieldDef.field)) {
          fieldDef.hide = true;
        }
      });
    },
    [parentConfigId],
  );

  const augRowsReq = useCallback(
    (rowsReq) => {
      rowsReq.hist = true;

      rowsReq.expr = ExprUtils.and(rowsReq.expr, ExprUtils.whereParams('id', parentConfigId));

      if (!fullHistory) {
        rowsReq.expr = ExprUtils.and(rowsReq.expr, ExprUtils.whereParams('status', ['ACTIVE', 'FAIL']));
      }
    },
    [fullHistory, parentConfigId],
  );

  const download = useCallback(
    (data) => {
      const downloadName = data.code;
      const job = {
        name: 'Download ' + downloadName,
        module,
        entity: 'CONFIG',
        entityId: data.id,
        rsrcCode: 'CONFIG_EXPORT_PLUGIN',
        reqParamJson: JSON.stringify({
          name: 'Download ' + downloadName,
          version: data.version,
        }),
        cat: 'EXPORT',
        queueExempt: true,
      };

      rsrc.save('skdr', 'JOB', job).then((savedJob) => {
        rsrc.start(savedJob.id);
      });
    },
    [module, rsrc],
  );

  const getContextMenuItems = useCallback(
    (params) => {
      let contextMenus = entityListRef.current.getContextMenuItems(params);

      if (parentConfigId != null && params.node.data?.verStartInstant && params.node.data?.verEndInstant) {
        const now = new Date();
        const verStartInstant = new Date(params.node.data.verStartInstant);
        const verEndInstant = new Date(params.node.data.verEndInstant);
        const currentRecord = verStartInstant < now && now < verEndInstant;

        contextMenus = contextMenus.map((contextMenuItem) => {
          if (contextMenuItem.name !== 'Details') return contextMenuItem;

          return {
            ...contextMenuItem,
            action: () =>
              navigateTo(parentOrgId ? module + '/org' : module + '/config', {
                mode: Mode.DETAIL,
                entityId: parentOrgId ?? params.node.data.id,
                asOfDate: currentRecord ? null : params.node.data.verEndInstant,
                selectedTabIndex: parentOrgId ? 1 : undefined,
              }),
          };
        });
      }

      contextMenus.push({
        name: 'Download',
        action: () => download(params.node.data),
        icon: fas.faArrowCircleDown,
      });
      return contextMenus;
    },
    [download, module, navigateTo, parentConfigId, parentOrgId],
  );

  return (
    <EntityList
      entity="CONFIG"
      ref={entityListRef}
      augFieldDefs={augFieldDefs}
      augRowsReq={augRowsReq}
      showTitle={showTitle}
      gridOptions={{ getContextMenuItems }}
    />
  );
};
